/**
 * SeedConfirmationScreen.js
 * Verification du code de recuperation par retape de 3 mots — PF-01 CP4.
 *
 * Contrat de securite (DEC-030, DEC-033) :
 *   - 3 positions tirees au sort a l'affichage, FIXES pour la session (Q4) :
 *     les regenerer a chaque erreur laisserait balayer la phrase par essais ;
 *   - le message d'erreur ne revele jamais quel mot est faux, ni le mot attendu ;
 *   - la comparaison se fait en NFKD pour absorber les differences de
 *     composition Unicode des accents (clavier, OS, copie), pas pour rendre la
 *     saisie tolerante aux accents manquants.
 * Le composant ne rend que son contenu : le parent fournit la mise en page.
 */

import React, { useState } from 'react';
import { G, inputSt, BtnPrimary, BtnBack, ErrBox, IconFrame, IconKey } from './onboardingAtoms';

const WORD_COUNT = 24;
const CHALLENGE_COUNT = 3;

/** Canonicalise un mot pour comparaison : NFKD, sans espaces parasites, minuscule. */
const canonical = (word) => word.normalize('NFKD').trim().toLowerCase();

/** Tire CHALLENGE_COUNT positions distinctes dans [0, WORD_COUNT - 1], triees. */
export function pickPositions() {
  const chosen = new Set();
  const buf = new Uint32Array(1);
  while (chosen.size < CHALLENGE_COUNT) {
    crypto.getRandomValues(buf);
    chosen.add(buf[0] % WORD_COUNT);
  }
  return [...chosen].sort((a, b) => a - b);
}

// confirmLabel : libellé optionnel du bouton final, pour réutiliser l'écran
// hors inscription (code de transmission PF-03). Défaut = PF-01.
const SeedConfirmationScreen = ({
  seedPhrase,
  positions,
  onConfirm,
  onBack,
  confirmLabel = 'Confirmer et créer mon coffre',
}) => {
  // Les positions sont tirees par le parent en meme temps que la seed : elles
  // survivent a un aller-retour vers l'ecran des 24 mots, et ne changent qu'avec
  // une nouvelle phrase (DEC-033 Q4).
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');

  const words = seedPhrase.trim().split(/\s+/);
  const complete = positions.every(p => (answers[p] || '').trim() !== '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!complete) return;

    const allCorrect = positions.every(
      p => canonical(answers[p] || '') === canonical(words[p] || '')
    );

    if (allCorrect) {
      onConfirm();
      return;
    }

    // Reset complet : on ne signale pas quel mot est faux.
    setAnswers({});
    setError('Un ou plusieurs mots ne correspondent pas. Vérifiez vos notes et réessayez.');
  };

  return (
    <>
      <BtnBack onClick={onBack}>← Retour au code</BtnBack>
      <IconFrame><IconKey /></IconFrame>

      <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
        Vérifions votre code
      </h2>
      <p style={{ margin: '0 0 1.75rem', color: G, fontSize: '0.9rem', lineHeight: 1.6 }}>
        Retapez les mots demandés ci-dessous pour vérifier que vous les avez bien
        notés. Respectez l'orthographe et les accents.
      </p>

      <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {positions.map(p => (
          <div key={p}>
            <label
              htmlFor={`seed-word-${p}`}
              style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}
            >
              Mot n° {p + 1}
            </label>
            <input
              id={`seed-word-${p}`}
              name={`seed-word-${p}`}
              type="text"
              value={answers[p] || ''}
              onChange={e => {
                const { value } = e.target;
                setAnswers(prev => ({ ...prev, [p]: value }));
                setError('');
              }}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              style={inputSt}
            />
          </div>
        ))}

        <ErrBox msg={error} />

        <BtnPrimary disabled={!complete}>{confirmLabel}</BtnPrimary>
      </form>
    </>
  );
};

export default SeedConfirmationScreen;
